// Persistent "active account" across the whole admin app. The URL (when
// under /accounts/:accountId/*) is the strongest signal; when the user
// navigates to a workspace-level page (Today, Wiki, Insights, Settings)
// the context is preserved so sidebar links + the banner still reflect
// the account they were working on.
//
// Mirrors the IG Field Suite's ProjectContext / lastProjectId pattern:
// URL is authority when present, localStorage (keyed per workspace) is the fallback.

import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { useMatch } from "react-router-dom";
import { useAuth } from "@/state/auth";
import { readLastAccount, writeLastAccount } from "@/lib/lastAccount";

type Ctx = {
  activeAccountId: number | null;
  setActiveAccountId: (id: number | null) => void;
};

const AccountContext = createContext<Ctx | null>(null);

const readInitial = readLastAccount;

export function AccountContextProvider({ children }: { children: ReactNode }) {
  const match = useMatch("/accounts/:accountId/*");
  const urlId = match?.params.accountId ? Number(match.params.accountId) : null;
  const ws = useAuth().activeAgency?.id ?? null;

  // The remembered id is tagged with the workspace it belongs to.
  const [state, setState] = useState<{ ws: string | null; id: number | null }>(
    () => ({ ws, id: urlId ?? readInitial(ws) })
  );
  // On an agency switch, read THAT workspace's remembered account. Adjusted during
  // render (not in an effect) so nothing renders with the old agency's account.
  // When auth has only just loaded (ws was null) the URL is trusted; on a real switch
  // an /accounts/:id still in the address bar belongs to the PREVIOUS agency, so it
  // is ignored rather than remembered under the new one.
  let current = state;
  if (state.ws !== ws) {
    current = { ws, id: (state.ws === null ? urlId : null) ?? readInitial(ws) };
    setState(current);
  }

  // URL wins whenever a route is under /accounts/:id.
  useEffect(() => {
    if (urlId && urlId !== current.id) setState({ ws, id: urlId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlId]);

  // Mirror to localStorage (per workspace) so a fresh tab lands "in" the same account.
  useEffect(() => {
    if (!state.ws || state.ws !== ws) return;
    writeLastAccount(state.ws, state.id);
  }, [state, ws]);

  const setActiveAccountId = (id: number | null) => setState({ ws, id });

  return (
    <AccountContext.Provider value={{ activeAccountId: current.id, setActiveAccountId }}>
      {children}
    </AccountContext.Provider>
  );
}

export function useActiveAccount(): Ctx {
  const c = useContext(AccountContext);
  if (!c) throw new Error("useActiveAccount must be inside AccountContextProvider");
  return c;
}
